import { createFileRoute } from "@tanstack/react-router";
import { useSiteData } from "@/lib/use-site-data";
import { openCookiePreferences, useConsent } from "@/lib/consent";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/legal/cookies")({
  head: () => ({
    meta: [
      { title: "Política de cookies · El Pajar de Benito" },
      { name: "description", content: "Qué cookies utiliza elpajardebenito.es y cómo configurarlas en tu navegador." },
      { property: "og:title", content: "Política de cookies · El Pajar de Benito" },
      { property: "og:description", content: "Uso de cookies técnicas imprescindibles en el sitio web del alojamiento." },
      { property: "og:url", content: "https://elpajardebenito.es/legal/cookies" },
      { name: "robots", content: "index, follow" },
    ],
    links: [{ rel: "canonical", href: "https://elpajardebenito.es/legal/cookies" }],
  }),
  component: Cookies,
});

function Cookies() {
  const { config } = useSiteData();
  const { state, hasDecision, reset } = useConsent();
  const hoy = new Date().toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" });
  return (
    <>
      <h1>Política de cookies</h1>
      <p className="text-sm text-muted-foreground">Última actualización: {hoy}</p>

      <h2>1. ¿Qué son las cookies?</h2>
      <p>Una cookie es un pequeño fichero de texto que un sitio web almacena en tu navegador cuando lo visitas. Permiten al sitio recordar información sobre tu visita, como el idioma preferido o el inicio de sesión, para facilitar la navegación.</p>

      <h2>2. Responsable y marco jurídico</h2>
      <p>
        El responsable del tratamiento es <strong>{config.nombre_fiscal || "CASA JUANA Y LUCIO S.L."}</strong>
        {config.nif ? <> (CIF {config.nif})</> : null}, con domicilio en {config.direccion}, {config.codigo_postal} {config.ciudad} ({config.pais}).
      </p>
      <p>
        El uso de cookies en este sitio se rige por el <strong>artículo 22.2 de la LSSI-CE</strong>, el
        <strong> Reglamento (UE) 2016/679 (RGPD)</strong>, la <strong>LOPDGDD 3/2018</strong>, las
        <strong> Directrices 03/2022 del CEPD</strong> y la <strong>Guía sobre el uso de cookies de la AEPD (nov. 2023)</strong>.
      </p>

      <h2>3. Categorías de cookies previstas</h2>
      <p>Utilizamos las siguientes categorías. Podrás activarlas o desactivarlas individualmente en cualquier momento desde el panel de preferencias:</p>
      <ul>
        <li><strong>Necesarias</strong> — imprescindibles para el funcionamiento y la seguridad. Exentas de consentimiento (art. 22.2 LSSI-CE).</li>
        <li><strong>Preferencias</strong> — recuerdan opciones del usuario (idioma, vistas). Requieren consentimiento.</li>
        <li><strong>Análisis</strong> — medición agregada de uso para mejorar el sitio. Requieren consentimiento.</li>
        <li><strong>Marketing</strong> — contenido embebido de terceros y medición de campañas. Requieren consentimiento y pueden implicar transferencias internacionales.</li>
      </ul>

      <h2>4. Cookies utilizadas actualmente</h2>
      <p>A día de hoy este sitio utiliza <strong>únicamente cookies técnicas propias imprescindibles</strong>. No se cargan cookies analíticas, publicitarias ni de terceros sin tu consentimiento previo.</p>

      <div className="my-4 overflow-x-auto rounded border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-left">
            <tr>
              <th className="p-2">Cookie</th>
              <th className="p-2">Finalidad</th>
              <th className="p-2">Duración</th>
              <th className="p-2">Categoría</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            <tr><td className="p-2">sb-*-auth-token</td><td className="p-2">Mantener la sesión iniciada del panel de gestión.</td><td className="p-2">Sesión / 1 semana</td><td className="p-2">Necesaria</td></tr>
            <tr><td className="p-2">cookie-consent-v1</td><td className="p-2">Guardar tus preferencias de consentimiento de cookies (evidencia obligatoria del RGPD).</td><td className="p-2">24 meses</td><td className="p-2">Necesaria</td></tr>
          </tbody>
        </table>
      </div>

      <h2>5. Cookies de terceros por servicios embebidos</h2>
      <p>Algunas páginas pueden incluir contenido embebido de terceros (por ejemplo, mapas o vídeos). Esos contenidos <strong>no se cargan</strong> hasta que aceptas la categoría correspondiente ("Marketing" o "Análisis"). Los proveedores tienen sus propias políticas de privacidad, que te recomendamos consultar.</p>

      <h2>6. Gestión de tu consentimiento</h2>
      <p>Puedes aceptar todas las cookies, rechazarlas todas o configurar tus preferencias por categoría. Tu decisión se conserva un máximo de <strong>24 meses</strong>; transcurrido ese plazo se te volverá a solicitar. Puedes cambiar o retirar tu consentimiento en cualquier momento, con la misma facilidad con la que lo diste:</p>
      <div className="my-4 flex flex-wrap gap-2 not-prose">
        <Button size="sm" onClick={openCookiePreferences}>Configurar cookies</Button>
        {hasDecision && (
          <Button size="sm" variant="outline" onClick={reset}>
            Retirar mi consentimiento
          </Button>
        )}
      </div>
      {state && (
        <p className="text-xs text-muted-foreground">
          Consentimiento registrado el {new Date(state.timestamp).toLocaleString("es-ES")} — versión {state.version}.
        </p>
      )}

      <h2>7. Cómo gestionar las cookies desde tu navegador</h2>
      <p>Puedes permitir, bloquear o eliminar las cookies instaladas en tu equipo mediante la configuración de las opciones de tu navegador:</p>
      <ul>
        <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener">Google Chrome</a></li>
        <li><a href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias" target="_blank" rel="noopener">Mozilla Firefox</a></li>
        <li><a href="https://support.apple.com/es-es/guide/safari/sfri11471/mac" target="_blank" rel="noopener">Safari</a></li>
        <li><a href="https://support.microsoft.com/es-es/microsoft-edge" target="_blank" rel="noopener">Microsoft Edge</a></li>
      </ul>
      <p>Ten en cuenta que la desactivación de determinadas cookies puede impedir el correcto funcionamiento del sitio o del panel de gestión.</p>

      <h2>8. Contacto</h2>
      <p>Si tienes cualquier duda sobre esta política de cookies, puedes escribirnos a <a href={`mailto:${config.email_contacto}`}>{config.email_contacto}</a>.</p>
    </>
  );
}